import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  forwardRef,
  Host,
  Inject,
  InjectionToken,
  OnDestroy,
  OnInit,
  Optional,
  SkipSelf,
} from '@angular/core';
import { FormControlStatus, NgControl } from '@angular/forms';
import { Unsubscribable } from 'rxjs';

import { environment } from 'environments/environment';
import { NgControlDirective } from './form-control-class.directive';
import { TypedValidationErrors } from './invalid-message-for.directive';

export const VALIDATION_CONTEXT = new InjectionToken<ValidationContextDirective>(
  'ValidationContext',
);

@Directive({
  selector: '[appValidationContext], [appFormControl]',
  providers: [
    {
      provide: VALIDATION_CONTEXT,
      useExisting: forwardRef(() => ValidationContextDirective),
    },
  ],
  standalone: false,
})
export class ValidationContextDirective implements OnInit, OnDestroy {
  get status(): FormControlStatus | null {
    return this.getValue(
      vc => vc.ngControl?.control.status ?? null,
      (previousStatus, currentStatus) => {
        for (const statusValue of ['DISABLED', 'PENDING', 'INVALID', 'VALID'] as const) {
          if (previousStatus === statusValue || currentStatus === statusValue) {
            return statusValue;
          }
        }
        return null;
      },
    );
  }

  get errors(): Partial<TypedValidationErrors> | null {
    return this.getValue(
      vc => (vc.ngControl?.control.errors ?? null) as TypedValidationErrors | null,
      (previousErrors, currentErrors) => {
        if (previousErrors != null && currentErrors != null) {
          return { ...previousErrors, ...currentErrors };
        }
        return currentErrors ?? previousErrors;
      },
    );
  }

  get touched(): boolean {
    if (this.ngControl?.control.touched) {
      return true;
    }
    return this.getValue(
      vc => vc.ngControl?.control.touched ?? false,
      (previousTouched, currentTouched) => currentTouched || previousTouched,
    );
  }

  get hasValue(): boolean {
    return this.getValue(
      vc => !!vc.ngControl?.control.value,
      (previousHasValue, currentHasValue) => currentHasValue || previousHasValue,
      /* ascending */ false,
    );
  }

  private children: ValidationContextDirective[] = [];

  private _subscriptions: Unsubscribable[] = [];
  private _isDisposed = false;

  constructor(
    private cd: ChangeDetectorRef,
    private el: ElementRef<HTMLElement>,
    @Inject(NgControl) @Optional() @Host() private ngControl?: NgControlDirective,
    @Inject(VALIDATION_CONTEXT) @Optional() @SkipSelf() private parent?: ValidationContextDirective,
  ) {}

  private getValue<T>(
    getter: (context: ValidationContextDirective) => T,
    aggregateResult: (previousResult: T, currentValue: T) => T,
    ascending = true,
  ): T {
    if (ascending && this.parent) {
      return this.parent.getValue(getter, aggregateResult);
    }

    let currentValue = getter(this);
    for (const child of this.children) {
      currentValue = aggregateResult(currentValue, child.getValue(getter, aggregateResult, false));
    }

    return currentValue;
  }

  ngOnInit() {
    if (this.parent) {
      this._subscriptions.push(this.parent.registerChild(this));
    }
    if (!this.ngControl) {
      const start = Date.now();
      const interval = setInterval(() => {
        if (this._isDisposed || this.children.length > 0) {
          clearInterval(interval);
          return;
        }

        if (Date.now() - start < 5000) {
          return;
        }

        clearInterval(interval);
        const match = /formcontrol(name)?="[^"]+"/.exec(this.el.nativeElement.outerHTML);
        environment.warn(
          'No children attached to ValidationContext with ' +
            (match ? 'form control: ' + match[0] : 'HTML: ' + this.el.nativeElement.outerHTML) +
            '\nMissing appFormControl on control',
        );
      }, 200);
    }
  }

  ngOnDestroy() {
    this._subscriptions.forEach(s => s.unsubscribe());
    this._isDisposed = true;
  }

  registerChild(child: ValidationContextDirective): Unsubscribable {
    this.children.push(child);
    this.cd.markForCheck();

    return {
      unsubscribe: () => {
        const index = this.children.indexOf(child);
        if (index >= 0) {
          this.children.splice(index, 1);
          this.cd.markForCheck();
        }
      },
    };
  }
}
